/**
 * Dashboard Data Fetching
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * License data fetching with caching, retry, and cancellation support.
 */

import { api } from "@/lib/http-client";
import { License } from "@/types";
import { logger } from "@/lib/logger";
import {
  apiCache,
  persistentCache,
  cacheKeys,
  CACHE_TTL,
} from "@/lib/cache-service";
import { withRetry } from "@/utils/retry";

/**
 * Fetch license data with caching.
 *
 * @param payment_intent - Payment intent ID to fetch licenses for. Accepts
 *   ``null`` so callers (lifetime-adapter, thanksAdapter) do not need a
 *   parallel narrowing branch — Stripe transitional invoices on the new API
 *   can return a null payment_intent and the adapter chain already passes
 *   that through. The internal early-return below makes the null path
 *   safe at runtime; widening the type makes BE-2's call sites compile-clean
 *   without a cast (S295/DD-4).
 * @param productId - Optional Stripe product ID for per-product filtering.
 *                    When provided, the backend filters by StripeProductId
 *                    to prevent cross-product contamination in multi-product orders.
 */
export const fetchLicense = async (
  payment_intent: string | null,
  productId?: string,
): Promise<License[]> => {
  if (!payment_intent) {
    logger.warn("fetchLicense called with empty payment_intent");
    return [];
  }

  // Include productId in cache key to prevent stale cross-product data
  const cacheKey = productId
    ? cacheKeys.license(`${payment_intent}:${productId}`)
    : cacheKeys.license(payment_intent);

  try {
    return await apiCache.getOrSet(
      cacheKey,
      async () => {
        const url = productId
          ? `/licenses/${payment_intent}?product_id=${encodeURIComponent(productId)}`
          : `/licenses/${payment_intent}`;
        const response = await api.get(url);
        const licenseData = response.data;

        if (!Array.isArray(licenseData)) {
          logger.error(
            `Invalid license data format for ${payment_intent}:`,
            licenseData,
          );
          return [];
        }

        return licenseData;
      },
      CACHE_TTL.SHORT, // 60 seconds — keeps license/device state current after purchases and deactivations
    );
  } catch (error) {
    logger.error(`Failed to fetch license data for ${payment_intent}:`, error);
    return [];
  }
};

/**
 * Fetch fresh license data bypassing cache
 * Use this when you need current device information (e.g., license details page)
 */
export const fetchFreshLicense = async (
  payment_intent: string,
): Promise<License[]> => {
  if (!payment_intent) {
    logger.warn("fetchFreshLicense called with empty payment_intent");
    return [];
  }

  const cacheKey = cacheKeys.license(payment_intent);

  try {
    apiCache.delete(cacheKey);

    const response = await api.get(`/licenses/${payment_intent}`);
    const licenseData = response.data;

    if (!Array.isArray(licenseData)) {
      logger.error(
        `Invalid license data format for ${payment_intent}:`,
        licenseData,
      );
      return [];
    }

    apiCache.set(cacheKey, licenseData, CACHE_TTL.SHORT); // 60 seconds — avoids stale device list after external deactivation

    return licenseData;
  } catch (error) {
    logger.error(
      `Failed to fetch fresh license data for ${payment_intent}:`,
      error,
    );
    return [];
  }
};

/**
 * Configuration options for license fetch with retry
 */
export interface FetchLicenseRetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  signal?: AbortSignal;
  onRetryAttempt?: (
    attempt: number,
    maxRetries: number,
    error: unknown,
  ) => void;
  onRetriesExhausted?: (error: unknown, attempts: number) => void;
  onSuccess?: (licenses: License[], attempts: number) => void;
  bypassCache?: boolean;
}

/**
 * Fetch license result with detailed status
 */
export interface FetchLicenseResult {
  success: boolean;
  licenses: License[];
  error?: unknown;
  attempts: number;
  cancelled: boolean;
}

/**
 * Fetch license data with automatic retry and exponential backoff.
 * Provides enhanced reliability with retry, cache integration, and cancellation.
 */
export const fetchLicenseWithRetry = async (
  payment_intent: string,
  options: FetchLicenseRetryOptions = {},
): Promise<FetchLicenseResult> => {
  const {
    maxRetries = 3,
    initialDelayMs = 1000,
    signal,
    onRetryAttempt,
    onRetriesExhausted,
    onSuccess,
    bypassCache = false,
  } = options;

  if (!payment_intent) {
    logger.warn("fetchLicenseWithRetry called with empty payment_intent");
    return {
      success: false,
      licenses: [],
      error: new Error("Payment intent is required"),
      attempts: 0,
      cancelled: false,
    };
  }

  const cacheKey = cacheKeys.license(payment_intent);

  if (!bypassCache) {
    const cached = apiCache.get<License[]>(cacheKey);
    if (cached && Array.isArray(cached) && cached.length > 0) {
      logger.debug(`License cache hit for ${payment_intent}`);
      onSuccess?.(cached, 0);
      return {
        success: true,
        licenses: cached,
        attempts: 0,
        cancelled: false,
      };
    }
  }

  const result = await withRetry<License[]>(
    async () => {
      const response = await api.get(`/licenses/${payment_intent}`);
      const licenseData = response.data;

      if (!Array.isArray(licenseData)) {
        logger.error(
          `Invalid license data format for ${payment_intent}:`,
          licenseData,
        );
        throw new Error("Invalid license data format");
      }

      return licenseData;
    },
    {
      maxRetries,
      initialDelayMs,
      maxDelayMs: 15000,
      enableJitter: true,
      signal,
      operationName: `fetchLicense(${payment_intent})`,
      onRetryAttempt: (attempt, max, error) => {
        logger.debug(`License fetch retry ${attempt}/${max}`, error);
        onRetryAttempt?.(attempt, max, error);
      },
      onRetriesExhausted: (error, attempts) => {
        logger.warn(
          `License fetch exhausted after ${attempts} attempts`,
          error,
        );
        onRetriesExhausted?.(error, attempts);
      },
      onSuccess: (licenses, attempts) => {
        logger.debug(`License fetch succeeded after ${attempts} attempt(s)`);
        if (Array.isArray(licenses) && licenses.length > 0) {
          apiCache.set(cacheKey, licenses, CACHE_TTL.LONG);
        }
        onSuccess?.(licenses as License[], attempts);
      },
    },
  );

  if (result.success && result.data) {
    return {
      success: true,
      licenses: result.data,
      attempts: result.attempts,
      cancelled: false,
    };
  }

  return {
    success: false,
    licenses: [],
    error: result.error,
    attempts: result.attempts,
    cancelled: result.cancelled,
  };
};

/**
 * Fetch all licenses with caching
 */
export const fetchLicenses = async (): Promise<License[]> => {
  const cacheKey = cacheKeys.licenses();

  try {
    return await apiCache.getOrSet(
      cacheKey,
      async () => {
        const response = await api.get("/licenses");
        const licenseData = response.data;

        if (!Array.isArray(licenseData)) {
          logger.error("Invalid licenses data format:", licenseData);
          return [];
        }

        return licenseData;
      },
      CACHE_TTL.MEDIUM,
    );
  } catch (error) {
    logger.error("Failed to fetch licenses:", error);
    return [];
  }
};

/**
 * Fetch product name with long-term caching
 */
export const fetchProductName = async (productId: string): Promise<string> => {
  if (!productId) {
    logger.warn("fetchProductName called with empty productId");
    return "N/A";
  }

  const cacheKey = cacheKeys.productName(productId);

  try {
    return await persistentCache.getOrSet(
      cacheKey,
      async () => {
        const response = await api.get(`/products/${productId}`);
        const productData = response.data.data;
        return productData?.name || "N/A";
      },
      CACHE_TTL.PERMANENT,
    );
  } catch (error) {
    logger.error(`Failed to fetch product data for ${productId}:`, error);
    return "N/A";
  }
};

/**
 * Fetch payment method with caching
 */
export const fetchPaymentMethod = async (paymentIntentId: string) => {
  if (!paymentIntentId) {
    logger.warn("fetchPaymentMethod called with empty paymentIntentId");
    return "N/A";
  }

  const cacheKey = cacheKeys.paymentMethod(paymentIntentId);

  try {
    return await apiCache.getOrSet(
      cacheKey,
      async () => {
        const response = await api.get(
          `prices/payment-method/${paymentIntentId}`,
        );
        return response.data;
      },
      CACHE_TTL.LONG,
    );
  } catch (error) {
    logger.error(
      `Failed to fetch payment method for ${paymentIntentId}:`,
      error,
    );
    return "N/A";
  }
};

/**
 * Fetch the recurring interval for a price ID.
 * F-4 (S343): Fallback for code paths where the Stripe price object was not
 * expanded inline.  Uses the authenticated api client — raw fetch() would
 * return HTTP 401 because the endpoint requires authentication.
 */
export const fetchPriceInterval = async (priceId: string): Promise<string | undefined> => {
  if (!priceId) return undefined;
  try {
    const response = await api.get(`prices/${priceId}`);
    return response.data?.recurring?.interval as string | undefined;
  } catch (error) {
    logger.error(`fetchPriceInterval failed for ${priceId}:`, error);
    return undefined;
  }
};
